import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import {
  AlertCircle, Check, RefreshCw, Plane, Trash2,
  Users, Timer
} from 'lucide-react';
import API_URL from '../config';
import InteractiveCalendar from '../components/InteractiveCalendar';
import TeamLeaveCalendarView from '../components/TeamLeaveCalendarView';
import AnonymousTeamLeaveCalendar from '../components/AnonymousTeamLeaveCalendar';

const API_BASE = `${API_URL}/api`;

function nextWorkDate(dateStr, workDays) {
  const days = Array.isArray(workDays) && workDays.length > 0 ? workDays : [0, 1, 2, 3, 4];
  let d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  const formatDate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const date = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${date}`;
  };
  for (let i = 0; i < 14; i++) {
    if (days.includes((d.getDay() + 6) % 7)) return formatDate(d);
    d.setDate(d.getDate() + 1);
  }
  return formatDate(d);
}

// end_datetime uses the "return date" convention when the full day flag is set:
// T00:00:00 means "employee returns at start of this day" — backend finds the
// last ouvrable day strictly before it and counts up to that.
// T12:00:00 (half-day return) is passed as-is; backend counts the return day as 0.5.
function buildLeaveDatetimes(startDate, endDate, period, workDays) {
  if (startDate === endDate) {
    if (period === 'morning')
      return { start_datetime: `${startDate}T00:00:00`, end_datetime: `${endDate}T12:00:00` };
    if (period === 'afternoon')
      return { start_datetime: `${startDate}T12:00:00`, end_datetime: `${nextWorkDate(endDate, workDays)}T00:00:00` };
    return { start_datetime: `${startDate}T00:00:00`, end_datetime: `${nextWorkDate(endDate, workDays)}T00:00:00` };
  }
  const startTime = period.startFull ? 'T00:00:00' : 'T12:00:00';
  if (period.endFull) {
    return { start_datetime: startDate + startTime, end_datetime: `${nextWorkDate(endDate, workDays)}T00:00:00` };
  }
  return { start_datetime: startDate + startTime, end_datetime: `${endDate}T12:00:00` };
}

const LEAVE_TYPES = [
  { value: 'paid_leave', label: 'Congés payés (CP)', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', calendarColor: 'bg-emerald-600' },
  { value: 'sick_leave', label: 'Arrêt maladie', color: 'bg-orange-100 text-orange-700 border-orange-200', calendarColor: 'bg-orange-600' },
  { value: 'work_accident', label: 'Accident du travail (AT)', color: 'bg-red-100 text-red-700 border-red-200', calendarColor: 'bg-red-600' },
  { value: 'unjustified', label: 'Absence injustifiée', color: 'bg-slate-100 text-slate-700 border-slate-200', calendarColor: 'bg-slate-900' },
  { value: 'compensatory', label: 'Repos compensateur (HS)', color: 'bg-purple-100 text-purple-700 border-purple-200', calendarColor: 'bg-purple-600' },
];

const getLeaveTypeConfig = (value) => LEAVE_TYPES.find(t => t.value === value) || LEAVE_TYPES[0];

const LeaveTypeBadge = ({ type }) => {
  const cfg = getLeaveTypeConfig(type);
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>{cfg.label}</span>;
};

const PeriodDropdown = ({ value, onChange, disabled }) => (
  <select 
    value={value} 
    onChange={(e) => onChange(e.target.value)}
    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-[#6E56CF] disabled:opacity-50"
    disabled={disabled}
  >
    <option value="full">Toute la journée</option>
    <option value="morning">Matin</option>
    <option value="afternoon">Après-midi</option>
  </select>
);

const MultiDayStartDropdown = ({ isFull, onChange, disabled }) => (
  <select 
    value={isFull ? 'full' : 'afternoon'} 
    onChange={(e) => onChange(e.target.value === 'full')}
    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-[#6E56CF] disabled:opacity-50"
    disabled={disabled}
  >
    <option value="full">Toute la journée</option>
    <option value="afternoon">Après-midi</option>
  </select>
);

const MultiDayEndDropdown = ({ isFull, onChange, disabled }) => (
  <select 
    value={isFull ? 'full' : 'morning'} 
    onChange={(e) => onChange(e.target.value === 'full')}
    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-[#6E56CF] disabled:opacity-50"
    disabled={disabled}
  >
    <option value="full">Toute la journée</option>
    <option value="morning">Matin</option>
  </select>
);


export default function Conges() {
  const { user, isManager } = useAuth();
  const { triggerRefresh } = useNotifications();
  const [activeTab, setActiveTab] = useState('personal');

  const [balance, setBalance] = useState(null);
  const [overtimeBalance, setOvertimeBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Selection Interactive
  const [selectionMode, setSelectionMode] = useState('none');
  const [hoverDate, setHoverDate] = useState(null);

  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    period: 'full',
    start_full: true, end_full: true,
    reason: '',
    leave_type: 'paid_leave',
  });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const [users, setUsers] = useState([]);
  const [allBalances, setAllBalances] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [managerDataLoading, setManagerDataLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserBalance, setSelectedUserBalance] = useState(null);
  const [selectedUserOvertimeBalance, setSelectedUserOvertimeBalance] = useState(null);
  
  const [managerSelectionMode, setManagerSelectionMode] = useState('none');
  const [managerHoverDate, setManagerHoverDate] = useState(null);
  const [managerFormData, setManagerFormData] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    period: 'full',
    start_full: true, end_full: true,
    reason: '',
    leave_type: 'paid_leave',
  });
  const [managerSubmitting, setManagerSubmitting] = useState(false);
  const [usersOrder, setUsersOrder] = useState([]);

  const [preview, setPreview] = useState({ days: 0, method: '', breakdown: [] });
  const [managerPreview, setManagerPreview] = useState({ days: 0, method: '', breakdown: [] });

  // --- NOUVEAU: ÉTATS DU CALENDRIER ÉQUIPE (MANAGER) ---
  const [teamCalendarMonth, setTeamCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [teamCalendarData, setTeamCalendarData] = useState(null);
  
  const fetchTeamCalendar = async (year, month) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/calendar/${year}/${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTeamCalendarData(await res.json());
    } catch (e) { console.error(e); }
  };

  // --- ÉTATS DU CALENDRIER ÉQUIPE ANONYMISÉ (TOUS LES EMPLOYÉS) ---
  const [anonCalendarMonth, setAnonCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [anonCalendarData, setAnonCalendarData] = useState(null);
  const [anonCalendarLoading, setAnonCalendarLoading] = useState(false);

  const fetchAnonCalendar = async (year, month) => {
    setAnonCalendarLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/calendar/anonymous/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAnonCalendarData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setAnonCalendarLoading(false);
    }
  };

  useEffect(() => {
    const savedOrder = localStorage.getItem('usersOrder');
    if (savedOrder) {
      try { setUsersOrder(JSON.parse(savedOrder)); } catch (e) {}
    }
  }, []);

  // Preview effect for employee form
  useEffect(() => {
    const { start_date, end_date, period, start_full, end_full, leave_type } = formData;
    if (!start_date || !end_date) { setPreview({ days: 0, method: '', breakdown: [] }); return; }

    const isSameDay = start_date === end_date;
    const { start_datetime, end_datetime } = buildLeaveDatetimes(
      start_date, end_date,
      isSameDay ? period : { startFull: start_full, endFull: end_full },
      workDays
    );

    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/leaves/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ start_datetime, end_datetime, leave_type: leave_type || 'paid_leave' }),
          signal: ctrl.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        setPreview({ days: Number(data.days), method: data.method, breakdown: data.breakdown });
      } catch (e) {
        if (e.name !== 'AbortError') console.error('preview failed', e);
      }
    }, 300);

    return () => { clearTimeout(timer); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.start_date, formData.end_date, formData.period, formData.start_full, formData.end_full, formData.leave_type]);

  // Preview effect for manager form
  useEffect(() => {
    const { start_date, end_date, period, start_full, end_full, leave_type } = managerFormData;
    if (!start_date || !end_date) { setManagerPreview({ days: 0, method: '', breakdown: [] }); return; }

    const isSameDay = start_date === end_date;
    const { start_datetime, end_datetime } = buildLeaveDatetimes(
      start_date, end_date,
      isSameDay ? period : { startFull: start_full, endFull: end_full },
      selectedUserWorkDays
    );

    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/leaves/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ start_datetime, end_datetime, leave_type: leave_type || 'paid_leave' }),
          signal: ctrl.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        setManagerPreview({ days: Number(data.days), method: data.method, breakdown: data.breakdown });
      } catch (e) {
        if (e.name !== 'AbortError') console.error('manager preview failed', e);
      }
    }, 300);

    return () => { clearTimeout(timer); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerFormData.start_date, managerFormData.end_date, managerFormData.period, managerFormData.start_full, managerFormData.end_full, managerFormData.leave_type]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const activeA = a.is_active !== false;
      const activeB = b.is_active !== false;
      if (activeA !== activeB) return activeA ? -1 : 1;
      if (usersOrder.length > 0) {
        const indexA = usersOrder.indexOf(a.id);
        const indexB = usersOrder.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
      }
      return (a.full_name || a.email).localeCompare(b.full_name || b.email);
    });
  }, [users, usersOrder]);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/balance`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setBalance(await res.json());
    } catch {}
  };

  const fetchOvertimeBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/overtime/balance`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setOvertimeBalance(await res.json());
    } catch {}
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/my-requests`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setRequests(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUsers(await res.json());
    } catch {}
  };

  const fetchAllBalances = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/balances`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAllBalances(await res.json());
    } catch {}
  };

  const fetchTeamRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/team-requests`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTeamRequests(await res.json());
    } catch {}
  };

  const fetchUserOvertimeBalance = async (userId) => {
    if (!userId) { setSelectedUserOvertimeBalance(null); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/overtime/balance/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSelectedUserOvertimeBalance(await res.json());
      else setSelectedUserOvertimeBalance(null);
    } catch { setSelectedUserOvertimeBalance(null); }
  };

  useEffect(() => {
    if (activeTab === 'team' && isManager) {
      fetchTeamCalendar(teamCalendarMonth.year, teamCalendarMonth.month);
    }
  }, [activeTab, isManager, teamCalendarMonth]);

  useEffect(() => {
    fetchAnonCalendar(anonCalendarMonth.year, anonCalendarMonth.month);
  }, [anonCalendarMonth]);

  useEffect(() => {
    fetchBalance(); fetchOvertimeBalance(); fetchRequests();
    if (isManager) {
      const load = async () => {
        setManagerDataLoading(true);
        try { await Promise.all([fetchUsers(), fetchAllBalances(), fetchTeamRequests()]); } 
        finally { setManagerDataLoading(false); }
      };
      load();
    }
  }, [user, isManager]);

  useEffect(() => {
    if (selectedUserId && allBalances.length > 0) {
      setSelectedUserBalance(allBalances.find(b => b.user_id === parseInt(selectedUserId)) || null);
    } else { setSelectedUserBalance(null); }
    if (selectedUserId) fetchUserOvertimeBalance(selectedUserId);
    else setSelectedUserOvertimeBalance(null);
  }, [selectedUserId, allBalances]);

  const enrichedTeamRequests = teamRequests.map(request => {
    const u = users.find(u => u.id === request.user_id);
    const bal = allBalances.find(b => b.user_id === request.user_id);
    const balanceBefore = bal ? parseFloat(bal.available_days) : null;
    // Only CP-type (paid_leave) consumes the leave balance
    const consumesCPReq = request.leave_type === 'paid_leave';
    const balanceAfter = balanceBefore !== null && consumesCPReq
      ? balanceBefore - parseFloat(request.days_count || 0)
      : balanceBefore;
    return {
      ...request,
      user_name: u?.full_name || `User ${request.user_id}`,
      user_email: u?.email || '',
      balance_before: balanceBefore,
      balance_after: balanceAfter,
    };
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-orange-100 text-orange-700 border-orange-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    const labels = { pending: 'En attente', approved: 'Approuvée', rejected: 'Rejetée', cancelled: 'Annulée' };
    return <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border-2 ${badges[status]}`}><AlertCircle className="w-3 h-3" />{labels[status]}</span>;
  };

  const fmtDays = (n) => {
    const v = parseFloat(n);
    if (isNaN(v)) return '0';
    return parseFloat(v.toFixed(2)).toString();
  };

  const daysRequested = preview.days;
  const leavesThatConsumeCP = ['paid_leave'];

  const workDays = user?.work_days || [0, 1, 2, 3, 4];
  const consumesCP = leavesThatConsumeCP.includes(formData.leave_type);
  const isCompensatory = formData.leave_type === 'compensatory';
  const balanceAfterRequest = balance ? parseFloat(balance.available_days) - (consumesCP ? daysRequested : 0) : 0;

  let selectedUserWorkDays = [0, 1, 2, 3, 4];
  if (selectedUserId) {
    const u = users.find(x => x.id.toString() === selectedUserId.toString());
    if (u && u.work_days) selectedUserWorkDays = u.work_days;
  }
  const managerDaysRequested = managerPreview.days;
  const managerConsumesCP = leavesThatConsumeCP.includes(managerFormData.leave_type);
  const isManagerCompensatory = managerFormData.leave_type === 'compensatory';
  const managerBalanceAfter = selectedUserBalance ? parseFloat(selectedUserBalance.available_days) - (managerConsumesCP ? managerDaysRequested : 0) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMessage(null);
    try {
      const token = localStorage.getItem('token');
      const isSameDay = formData.start_date === formData.end_date;
      const dtBounds = buildLeaveDatetimes(formData.start_date, formData.end_date, isSameDay ? formData.period : { startFull: formData.start_full, endFull: formData.end_full }, workDays);
      
      const res = await fetch(`${API_BASE}/leaves/request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dtBounds, reason: formData.reason || undefined, leave_type: formData.leave_type }),
      });
      const responseData = await res.json();
      if (!res.ok) {
        let msg = 'Erreur lors de la création';
        if (typeof responseData.detail === 'string') {
          msg = responseData.detail;
        } else if (Array.isArray(responseData.detail)) {
          msg = responseData.detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else if (responseData.detail && typeof responseData.detail === 'object') {
          msg = responseData.detail.message || JSON.stringify(responseData.detail);
        }
        throw new Error(msg);
      }
      
      setFormData({ start_date: '', end_date: '', period: 'full', start_full: true, end_full: true, reason: '', leave_type: 'paid_leave' });
      setSelectionMode('none');
      if (responseData.is_anticipated) setSuccessMessage({ type: 'anticipated', days: responseData.days_count });
      else setSuccessMessage({ type: 'success' });
      
      await fetchBalance(); await fetchOvertimeBalance(); await fetchRequests();
      fetchAnonCalendar(anonCalendarMonth.year, anonCalendarMonth.month);
      triggerRefresh();
    } catch (err) { alert(err.message); } finally { setSubmitting(false); }
  };

  const handleManagerSubmit = async (e) => {
    e.preventDefault();
    setManagerSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const isSameDayMgr = managerFormData.start_date === managerFormData.end_date;
      const dtBoundsMgr = buildLeaveDatetimes(managerFormData.start_date, managerFormData.end_date, isSameDayMgr ? managerFormData.period : { startFull: managerFormData.start_full, endFull: managerFormData.end_full }, selectedUserWorkDays);
      
      const res = await fetch(`${API_BASE}/leaves/request/employee`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(managerFormData.user_id), ...dtBoundsMgr, reason: managerFormData.reason || undefined, leave_type: managerFormData.leave_type }),
      });
      const responseData = await res.json();
      if (!res.ok) {
        let msg = 'Erreur';
        if (typeof responseData.detail === 'string') {
          msg = responseData.detail;
        } else if (Array.isArray(responseData.detail)) {
          msg = responseData.detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else if (responseData.detail && typeof responseData.detail === 'object') {
          msg = responseData.detail.message || JSON.stringify(responseData.detail);
        }
        throw new Error(msg);
      }
      
      setManagerFormData({ user_id: '', start_date: '', end_date: '', period: 'full', start_full: true, end_full: true, reason: '', leave_type: 'paid_leave' });
      setSelectedUserId('');
      setManagerSelectionMode('none');
      
      await fetchAllBalances(); await fetchTeamRequests();
      triggerRefresh();
      alert(responseData.is_anticipated ? 'Congé créé et auto-approuvé par anticipation !' : 'Congé créé et auto-approuvé !');
    } catch (err) { alert(err.message); } finally { setManagerSubmitting(false); }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/${requestId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erreur de l'annulation");
      await fetchRequests();
    } catch (err) { alert(err.message); }
  };

  const handleApprove = async (requestId) => {
    setManagerDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/${requestId}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erreur");
      await fetchAllBalances(); await fetchTeamRequests(); triggerRefresh();
    } catch (err) { alert(err.message); } finally { setManagerDataLoading(false); }
  };

  const handleReject = async (requestId) => {
    const comment = window.prompt("Raison du refus (optionnelle) :");
    if (comment === null) return;
    setManagerDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/${requestId}/reject`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ manager_comment: comment || null }),
      });
      if (!res.ok) throw new Error("Erreur");
      await fetchAllBalances(); await fetchTeamRequests(); triggerRefresh();
    } catch (err) { alert(err.message); } finally { setManagerDataLoading(false); }
  };

  const handleManagerCancel = async (requestId) => {
    if (!window.confirm('Annuler cette demande ? Le solde sera restauré.')) return;
    setManagerDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/leaves/${requestId}/cancel`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erreur");
      await fetchAllBalances(); await fetchTeamRequests(); triggerRefresh();
    } catch (err) { alert(err.message); } finally { setManagerDataLoading(false); }
  };

  const filteredRequests = requests.filter(req => filter === 'all' || req.status === filter);

  const handleDateClick = (dateStr, fm, setFm, sm, setSm) => {
    if (sm === 'none' || sm === 'end') {
      setFm({ ...fm, start_date: dateStr, end_date: dateStr, period: 'full', start_full: true, end_full: true });
      setSm('start');
    } else if (sm === 'start') {
      if (dateStr < fm.start_date) {
        setFm({ ...fm, start_date: dateStr, end_date: dateStr });
      } else {
        setFm({ ...fm, end_date: dateStr });
        setSm('end');
      }
    }
  };


  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F8F9FD]">
      {/* Sidebar Interne (Bleu nuit #1A1B4B) */}
      {/* Main Content Area */}
      <div className="flex-1 p-4 md:p-10 mx-auto w-full max-w-6xl">
         {isManager && (
            <div className="flex justify-center mb-10 w-full relative">
               {/* Tabs */}
               
               <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row w-full sm:w-auto mx-auto z-10 gap-1 sm:gap-0">
                  <button onClick={() => setActiveTab('personal')} className={`flex justify-center items-center gap-2 px-4 py-3 sm:px-8 rounded-xl text-sm font-bold transition-all ${activeTab === 'personal' ? 'bg-[#1A1B4B] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                     <Plane className="w-4 h-4" /> Mes Congés
                  </button>
                  <button onClick={() => setActiveTab('team')} className={`flex justify-center items-center gap-2 px-4 py-3 sm:px-8 rounded-xl text-sm font-bold transition-all ${activeTab === 'team' ? 'bg-[#1A1B4B] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                     <Users className="w-4 h-4" /> Gestion Équipe
                  </button>
               </div>
            </div>
         )}
         
         <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 tracking-tight">
               {activeTab === 'personal' ? "Mes Congés" : "Gestion de l'équipe"}
            </h2>
            <button
              onClick={() => { 
                fetchBalance(); fetchOvertimeBalance(); fetchRequests(); 
                fetchAnonCalendar(anonCalendarMonth.year, anonCalendarMonth.month);
                if (isManager) { 
                  fetchUsers(); fetchAllBalances(); fetchTeamRequests(); 
                  fetchTeamCalendar(teamCalendarMonth.year, teamCalendarMonth.month);
                } 
              }}
              className="w-full sm:w-auto justify-center px-4 py-2 bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm rounded-xl font-medium transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
         </div>

         {activeTab === 'personal' && (
           <div className="space-y-8 max-w-[1100px] mx-auto pb-20">
              {/* Balances */}
              <div className="flex flex-col sm:flex-row gap-4">
                {balance && (
                  <div className="flex-1 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex items-center justify-between">
                    <div>
                       <div className="text-gray-500 font-medium text-sm mb-1 uppercase tracking-wide">Congés Payés</div>
                       <div className={`text-4xl font-extrabold tracking-tight ${parseFloat(balance.available_days) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                         {fmtDays(balance.available_days)} <span className="text-lg font-medium text-gray-400">jours</span>
                       </div>
                    </div>
                    {parseFloat(balance.anticipated_days) > 0 && <span className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full border border-orange-100">dont {fmtDays(balance.anticipated_days)} anticipés</span>}
                  </div>
                )}
                {overtimeBalance && (
                   <div className="flex-1 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex items-center justify-between">
                    <div>
                       <div className="text-gray-500 font-medium text-sm mb-1 uppercase tracking-wide">Heures Supp.</div>
                       <div className="text-4xl font-extrabold tracking-tight text-gray-800">
                         {parseFloat(overtimeBalance.accumulated_hours).toFixed(1)} <span className="text-lg font-medium text-gray-400">h</span>
                       </div>
                    </div>
                    <Timer className="w-8 h-8 text-purple-200" />
                   </div>
                )}
              </div>

              {/* Leave Request Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative">
                 <form onSubmit={handleSubmit}>
                    
                    {/* Top filters row in the card */}
                    <div className="flex flex-col md:flex-row items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                       <div className="flex items-center gap-3 w-full md:w-auto">
                          <select
                            value={formData.leave_type}
                            onChange={e => setFormData({ ...formData, leave_type: e.target.value })}
                            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-xl focus:ring-[#6E56CF] focus:border-[#6E56CF] block w-full p-2.5 font-medium shadow-sm transition-all"
                          >
                            {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                       </div>
                       
                       <div className="text-sm text-gray-400 flex items-center gap-4 mt-4 md:mt-0">
                          <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Jours dispos : {balance ? fmtDays(balance.available_days) : 0}j</span>
                       </div>
                    </div>

                    <div className="p-6">
                       <InteractiveCalendar 
                         existingRequests={requests}
                         startDate={formData.start_date}
                         endDate={formData.end_date}
                         hoverDate={hoverDate}
                         onDateHover={setHoverDate}
                         onDateClick={d => handleDateClick(d, formData, setFormData, selectionMode, setSelectionMode)}
                         workDays={workDays}
                       />
                    </div>

                    {/* Bottom Validation Band */}
                    <div className="bg-gray-50 p-4 md:p-6 flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-4 border-t border-gray-100">
                       <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-2 sm:flex sm:flex-row sm:items-end gap-2">
                             <div className="flex flex-col">
                                <label className="text-xs text-gray-500 font-semibold mb-1 ml-1">Du</label>
                                <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-colors focus-within:border-[#6E56CF] focus-within:ring-1 focus-within:ring-[#6E56CF]">
                                   <input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="px-2 py-2 border-r border-gray-100 text-xs sm:text-sm focus:outline-none w-full min-w-0" required />
                                   {formData.start_date === formData.end_date ? (
                                      <PeriodDropdown value={formData.period} onChange={v => setFormData({...formData, period: v})} disabled={!formData.start_date} />
                                   ) : (
                                      <MultiDayStartDropdown isFull={formData.start_full} onChange={v => setFormData({...formData, start_full: v})} disabled={!formData.start_date} />
                                   )}
                                </div>
                             </div>
                             <div className="flex flex-col">
                                <label className="text-xs text-gray-500 font-semibold mb-1 ml-1">Au</label>
                                <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-colors focus-within:border-[#6E56CF] focus-within:ring-1 focus-within:ring-[#6E56CF]">
                                   <input type="date" value={formData.end_date} min={formData.start_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="px-2 py-2 border-r border-gray-100 text-xs sm:text-sm focus:outline-none w-full min-w-0" required />
                                   {formData.start_date !== formData.end_date && (
                                     <MultiDayEndDropdown isFull={formData.end_full} onChange={v => setFormData({...formData, end_full: v})} disabled={!formData.end_date} />
                                   )}
                                </div>
                             </div>
                          </div>
                          
                          <div className="mt-3">
                            {preview.method === 'jours_ouvrables' && (
                              <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-100 mb-2">
                                ℹ️ <strong>Jours ouvrables (lun–sam)</strong> : tous les jours ouvrables jusqu'à la veille de votre reprise sont décomptés, samedi inclus.
                              </div>
                            )}
                            <input value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} type="text" placeholder="Raison (optionnel)..." className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#6E56CF] shadow-sm mb-2" />
                            {isCompensatory && overtimeBalance && (
                              parseFloat(overtimeBalance.accumulated_hours) > 0 ? (
                                <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded-lg border border-purple-100">
                                  💡 Ce congé sera converti et déduit de votre solde d'Heures Supplémentaires. ({parseFloat(overtimeBalance.accumulated_hours).toFixed(1)}h dispo)
                                </div>
                              ) : (
                                <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded-lg border border-orange-100">
                                  ⚠️ Vous n'avez aucune heure supplémentaire disponible à convertir.
                                </div>
                              )
                            )}
                            {!isCompensatory && consumesCP && balance && balanceAfterRequest < 0 && daysRequested > 0 && (
                              <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded-lg border border-orange-100">
                                ⚠️ Nouveau solde estimé : {fmtDays(balanceAfterRequest)}j (Congé anticipé)
                              </div>
                            )}
                          </div>
                          
                          {successMessage && (
                            <div className={`mt-3 p-3 rounded-lg text-sm flex items-center gap-2 border ${successMessage.type === 'anticipated' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                              <Check className="w-4 h-4 flex-shrink-0" />
                              {successMessage.type === 'anticipated' ? "Demande en attente (anticipée incluse)" : "Demande soumise avec succès !"}
                            </div>
                          )}
                       </div>
                       
                       <div className="flex flex-row xl:flex-col items-center xl:items-end justify-between gap-3 border-t xl:border-t-0 xl:border-l border-gray-200 pt-4 xl:pt-0 xl:pl-6">
                          <div>
                            <div className="text-gray-500 font-medium text-xs">Jours décomptés</div>
                            <div className="text-3xl font-extrabold text-[#1A1B4B] tracking-tight">{fmtDays(daysRequested)} <span className="text-lg text-gray-400 font-medium">J</span></div>
                            {preview.method && <div className="text-xs text-gray-400 mt-0.5">{preview.method.replace(/_/g, ' ')}</div>}
                          </div>
                          <button
                            type="submit"
                            disabled={submitting || daysRequested === 0}
                            className={`flex-shrink-0 py-3 px-5 rounded-xl text-white text-sm font-bold transition-all shadow-md focus:ring-4 focus:ring-[#6E56CF]/30 ${
                              consumesCP && balance && balanceAfterRequest < 0
                                ? 'bg-orange-500 hover:bg-orange-600 disabled:opacity-50'
                                : 'bg-[#6E56CF] hover:bg-[#5b46b3] disabled:opacity-50'
                            }}`}
                          >
                            {submitting ? 'Envoi...' : 'Poursuivre'}
                          </button>
                       </div>
                    </div>
                 </form>
              </div>

              {/* Anonymous Team Absences Calendar */}
              <div className="mt-8">
                 <AnonymousTeamLeaveCalendar
                   calendarData={anonCalendarData}
                   currentYear={anonCalendarMonth.year}
                   currentMonth={anonCalendarMonth.month}
                   loading={anonCalendarLoading}
                   onPrevMonth={() => setAnonCalendarMonth(prev => ({ year: prev.month === 1 ? prev.year - 1 : prev.year, month: prev.month === 1 ? 12 : prev.month - 1 }))}
                   onNextMonth={() => setAnonCalendarMonth(prev => ({ year: prev.month === 12 ? prev.year + 1 : prev.year, month: prev.month === 12 ? 1 : prev.month + 1 }))}
                   onToday={() => {
                     const d = new Date();
                     setAnonCalendarMonth({ year: d.getFullYear(), month: d.getMonth() + 1 });
                   }}
                 />
              </div>

              {/* History Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-8">
                 <h3 className="text-xl font-bold text-gray-800 mb-6">Demandes de congés</h3>
                 <div className="flex flex-wrap gap-2 mb-6">
                    {['all', 'pending', 'approved', 'rejected', 'cancelled'].map(f => (
                      <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-sm ${filter === f ? 'bg-[#1A1B4B] text-white' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`}>
                        {f === 'all' ? 'Toutes' : f === 'pending' ? 'En attente' : f === 'approved' ? 'Approuvées' : f === 'rejected' ? 'Rejetées' : 'Annulées'}
                        <span className={`ml-2 px-2 py-0.5 rounded-md text-xs ${filter === f ? 'bg-white/20' : 'bg-gray-200 text-gray-500'}`}>{requests.filter(r => f === 'all' || r.status === f).length}</span>
                      </button>
                    ))}
                 </div>
                 
                 {loading ? <div className="py-12 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin text-[#6E56CF]" /></div> : (
                    <>
                      {/* Mobile: cartes */}
                      <div className="md:hidden space-y-3">
                        {filteredRequests.length === 0 && <p className="py-8 text-center text-gray-400 text-sm">Aucune demande trouvée.</p>}
                        {filteredRequests.map(req => {
                          const s = new Date(req.start_datetime ? req.start_datetime.slice(0,10) : req.start_date).toLocaleDateString('fr-FR');
                          const e = new Date(req.end_datetime   ? req.end_datetime.slice(0,10)   : req.end_date).toLocaleDateString('fr-FR');
                          return (
                            <div key={req.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <LeaveTypeBadge type={req.leave_type} />
                                {getStatusBadge(req.status)}
                              </div>
                              <div className="text-sm text-gray-600">{s} → {e} <span className="ml-2 font-bold text-gray-800">{fmtDays(req.days_count)} j</span></div>
                              {req.is_anticipated && <span className="self-start px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200 uppercase">Anticipé</span>}
                              {req.status === 'pending' && (
                                <button onClick={() => handleCancel(req.id)} className="self-end flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors">
                                  <Trash2 className="w-3 h-3" /> Annuler
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Desktop: tableau */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                           <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
                              <tr className="border-b border-gray-200">
                                 <th className="py-4 pl-4 text-left rounded-tl-xl">Type</th>
                                 <th className="py-4 px-2 text-left">Dates</th>
                                 <th className="py-4 px-2 text-left">Jours</th>
                                 <th className="py-4 px-2 text-left">Statut</th>
                                 <th className="py-4 px-2 pl-6 text-right rounded-tr-xl">Action</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100 text-sm">
                              {filteredRequests.map(req => (
                                 <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="py-4 pl-4 pr-2"><LeaveTypeBadge type={req.leave_type} />{req.is_anticipated && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200 uppercase">Anticipé</span>}</td>
                                    <td className="py-4 px-2 font-medium text-gray-700 whitespace-nowrap">{new Date(req.start_datetime ? req.start_datetime.slice(0,10) : req.start_date).toLocaleDateString('fr-FR')} – {new Date(req.end_datetime ? req.end_datetime.slice(0,10) : req.end_date).toLocaleDateString('fr-FR')}</td>
                                    <td className="py-4 px-2 font-bold text-gray-800">{fmtDays(req.days_count)} j</td>
                                    <td className="py-4 px-2">{getStatusBadge(req.status)}</td>
                                    <td className="py-4 pr-4 pl-6 text-right">{req.status === 'pending' && <button onClick={() => handleCancel(req.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-5 h-5" /></button>}</td>
                                 </tr>
                              ))}
                              {filteredRequests.length === 0 && <tr><td colSpan="5" className="py-12 text-center text-gray-400">Aucune demande trouvée.</td></tr>}
                           </tbody>
                        </table>
                      </div>
                    </>
                 )}
              </div>
           </div>
         )}

         {activeTab === 'team' && isManager && (
           <div className="space-y-8 max-w-[1100px] mx-auto pb-20">
              {/* Add Leave for Employee */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative">
                 <form onSubmit={handleManagerSubmit}>
                    
                    <div className="flex flex-col md:flex-row items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                       <div className="w-full md:w-1/3 min-w-[250px]">
                          <select
                            value={managerFormData.user_id}
                            onChange={e => { setManagerFormData({ ...managerFormData, user_id: e.target.value }); setSelectedUserId(e.target.value); }}
                            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-xl focus:ring-[#6E56CF] focus:border-[#6E56CF] block w-full p-2.5 font-medium shadow-sm transition-all"
                            required
                          >
                             <option value="">Sélectionner l'employé...</option>
                            {sortedUsers.map(u => (
                              <option key={u.id} value={u.id} className={u.is_active === false ? 'line-through text-gray-400 italic' : ''}>
                                {u.full_name} ({u.email})
                              </option>
                            ))}
                          </select>

                           {managerFormData.user_id && (
                             <div className="mt-3 flex flex-wrap items-center gap-3 text-sm font-medium">
                               <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                                 <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">CP :</span>
                                 <span className={`font-extrabold ${selectedUserBalance && parseFloat(selectedUserBalance.available_days) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                   {selectedUserBalance ? fmtDays(selectedUserBalance.available_days) : 0} <span className="text-[10px] text-gray-400 font-normal">j</span>
                                 </span>
                               </div>
                               <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                                 <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">HS :</span>
                                 <span className={`font-extrabold ${selectedUserOvertimeBalance && parseFloat(selectedUserOvertimeBalance.accumulated_hours) < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                   {selectedUserOvertimeBalance ? parseFloat(selectedUserOvertimeBalance.accumulated_hours).toFixed(1) : 0} <span className="text-[10px] text-gray-400 font-normal">h</span>
                                 </span>
                               </div>
                             </div>
                           )}
                       </div>
                       <div className="w-full md:w-1/3 min-w-[200px] mt-4 md:mt-0">
                          <select
                            value={managerFormData.leave_type}
                            onChange={e => setManagerFormData({ ...managerFormData, leave_type: e.target.value })}
                            className="bg-white border border-gray-300 text-gray-700 text-sm rounded-xl focus:ring-[#6E56CF] focus:border-[#6E56CF] block w-full p-2.5 font-medium shadow-sm transition-all"
                          >
                            {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                       </div>
                    </div>

                    <div className="p-6">
                       <InteractiveCalendar 
                         existingRequests={managerFormData.user_id ? teamRequests.filter(r => r.user_id === parseInt(managerFormData.user_id)) : []}
                         startDate={managerFormData.start_date}
                         endDate={managerFormData.end_date}
                         hoverDate={managerHoverDate}
                         onDateHover={setManagerHoverDate}
                         onDateClick={d => handleDateClick(d, managerFormData, setManagerFormData, managerSelectionMode, setManagerSelectionMode)}
                         workDays={selectedUserWorkDays}
                       />
                    </div>

                    {/* Bottom Validation Band */}
                    <div className="bg-gray-50 p-4 md:p-6 flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-4 border-t border-gray-100">
                       <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-2 sm:flex sm:flex-row sm:items-end gap-2">
                             <div className="flex flex-col">
                                <label className="text-xs text-gray-500 font-semibold mb-1 ml-1">Du</label>
                                <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-colors focus-within:border-[#6E56CF] focus-within:ring-1 focus-within:ring-[#6E56CF]">
                                   <input type="date" value={managerFormData.start_date} onChange={e => setManagerFormData({...managerFormData, start_date: e.target.value})} className="px-2 py-2 border-r border-gray-100 text-xs sm:text-sm focus:outline-none w-full min-w-0" required />
                                   {managerFormData.start_date === managerFormData.end_date ? (
                                      <PeriodDropdown value={managerFormData.period} onChange={v => setManagerFormData({...managerFormData, period: v})} disabled={!managerFormData.start_date} />
                                   ) : (
                                      <MultiDayStartDropdown isFull={managerFormData.start_full} onChange={v => setManagerFormData({...managerFormData, start_full: v})} disabled={!managerFormData.start_date} />
                                   )}
                                </div>
                             </div>
                             <div className="flex flex-col">
                                <label className="text-xs text-gray-500 font-semibold mb-1 ml-1">Au</label>
                                <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-colors focus-within:border-[#6E56CF] focus-within:ring-1 focus-within:ring-[#6E56CF]">
                                   <input type="date" value={managerFormData.end_date} min={managerFormData.start_date} onChange={e => setManagerFormData({...managerFormData, end_date: e.target.value})} className="px-2 py-2 border-r border-gray-100 text-xs sm:text-sm focus:outline-none w-full min-w-0" required />
                                   {managerFormData.start_date !== managerFormData.end_date && (
                                     <MultiDayEndDropdown isFull={managerFormData.end_full} onChange={v => setManagerFormData({...managerFormData, end_full: v})} disabled={!managerFormData.end_date} />
                                   )}
                                </div>
                             </div>
                          </div>
                          
                          <div className="mt-3">
                            {managerPreview.method === 'jours_ouvrables' && (
                              <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-100 mb-2">
                                ℹ️ <strong>Jours ouvrables (lun–sam)</strong> : tous les jours ouvrables jusqu'à la veille de votre reprise sont décomptés, samedi inclus.
                              </div>
                            )}
                            <input value={managerFormData.reason} onChange={e => setManagerFormData({...managerFormData, reason: e.target.value})} type="text" placeholder="Raison manager..." className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#6E56CF] shadow-sm" />
                            {isManagerCompensatory && selectedUserOvertimeBalance && (
                              <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded-lg border border-purple-100 mt-2">
                                💡 Ce congé sera déduit des Heures Supplémentaires de l'employé.
                              </div>
                            )}
                            {!isManagerCompensatory && selectedUserBalance && managerBalanceAfter < 0 && managerDaysRequested > 0 && (
                              <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded-lg border border-orange-100 mt-2">
                                ⚠️ Nouveau solde estimé : {fmtDays(managerBalanceAfter)}j (Congé anticipé)
                              </div>
                            )}
                          </div>
                       </div>
                       
                       <div className="flex flex-row xl:flex-col items-center xl:items-end justify-between gap-3 border-t xl:border-t-0 xl:border-l border-gray-200 pt-4 xl:pt-0 xl:pl-6">
                          <div>
                            <div className="text-gray-500 font-medium text-xs">Action directe</div>
                            <div className="text-3xl font-extrabold text-[#1A1B4B] tracking-tight">{fmtDays(managerDaysRequested)} <span className="text-lg text-gray-400 font-medium">J</span></div>
                            {managerPreview.method && <div className="text-xs text-gray-400 mt-0.5">{managerPreview.method.replace(/_/g, ' ')}</div>}
                          </div>
                          <button
                            type="submit"
                            disabled={managerSubmitting || managerDaysRequested === 0 || !managerFormData.user_id}
                            className={`flex-shrink-0 py-3 px-5 rounded-xl text-white text-sm font-bold transition-all shadow-md focus:ring-4 focus:ring-[#6E56CF]/30 bg-[#6E56CF] hover:bg-[#5b46b3] disabled:opacity-50`}
                          >
                            {managerSubmitting ? 'Création...' : 'Créer & Approuver'}
                          </button>
                       </div>
                    </div>
                 </form>
              </div>

              {/* Monthly Team Calendar */}
              <div className="mt-8">
                 <TeamLeaveCalendarView 
                   calendarData={teamCalendarData}
                   currentYear={teamCalendarMonth.year}
                   currentMonth={teamCalendarMonth.month}
                   onPrevMonth={() => setTeamCalendarMonth(prev => ({ year: prev.month === 1 ? prev.year - 1 : prev.year, month: prev.month === 1 ? 12 : prev.month - 1 }))}
                   onNextMonth={() => setTeamCalendarMonth(prev => ({ year: prev.month === 12 ? prev.year + 1 : prev.year, month: prev.month === 12 ? 1 : prev.month + 1 }))}
                 />
              </div>

              {/* Team requests */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-8">
                 <h3 className="text-xl font-bold text-gray-800 mb-6">Demandes de l'équipe</h3>
                 
                 {managerDataLoading && enrichedTeamRequests.length === 0 ? <div className="py-12 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin text-[#6E56CF]" /></div> : (
                   <>
                     {/* Mobile: cartes */}
                     <div className="md:hidden space-y-3">
                       {enrichedTeamRequests.length === 0 && <p className="py-8 text-center text-gray-400 text-sm">Aucune demande trouvée.</p>}
                       {enrichedTeamRequests.map(req => {
                         const s = new Date(req.start_datetime ? req.start_datetime.slice(0,10) : req.start_date).toLocaleDateString('fr-FR');
                         const e = new Date(req.end_datetime   ? req.end_datetime.slice(0,10)   : req.end_date).toLocaleDateString('fr-FR');
                         return (
                           <div key={req.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
                             <div className="flex items-center justify-between gap-2 flex-wrap">
                               <span className="font-bold text-gray-800 text-sm">{req.user_name}</span>
                               {getStatusBadge(req.status)}
                             </div>
                             <div className="flex items-center gap-2 flex-wrap">
                               <LeaveTypeBadge type={req.leave_type} />
                               {req.is_anticipated && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200 uppercase">Anticipé</span>}
                             </div>
                             <div className="text-sm text-gray-600">{s} → {e} <span className="ml-2 font-bold text-gray-800">{fmtDays(req.days_count)} j</span></div>
                             {req.leave_type === 'paid_leave' && req.balance_before !== null && (
                               <div className="flex items-center gap-2 text-xs bg-white rounded-lg px-3 py-2 border border-gray-200">
                                 <span className="text-gray-500">Solde CP&nbsp;:</span>
                                 <span className="font-bold text-gray-700">{fmtDays(req.balance_before)} j</span>
                                 <span className="text-gray-400">→</span>
                                 <span className={`font-bold ${req.balance_after < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmtDays(req.balance_after)} j</span>
                                 {req.balance_after < 0 && <span className="text-[10px] font-bold bg-red-100 text-red-600 border border-red-200 rounded px-1 py-0.5">⚠️</span>}
                               </div>
                             )}
                             <div className="flex justify-end gap-2 mt-1">
                               {req.status === 'pending' && (
                                 <>
                                   <button onClick={() => handleApprove(req.id)} className="px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg text-xs font-bold transition-colors">Approuver</button>
                                   <button onClick={() => handleReject(req.id)} className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs font-bold transition-colors">Refuser</button>
                                 </>
                               )}
                               {req.status !== 'cancelled' && req.status !== 'rejected' && (
                                 <button onClick={() => handleManagerCancel(req.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                               )}
                             </div>
                           </div>
                         );
                       })}
                     </div>
                     {/* Desktop: tableau */}
                     <div className="hidden md:block overflow-x-auto">
                       <table className="w-full">
                          <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
                             <tr className="border-b border-gray-200">
                                <th className="py-4 pl-4 text-left rounded-tl-xl min-w-[150px]">Employé</th>
                                <th className="py-4 px-2 text-left min-w-[120px]">Type</th>
                                <th className="py-4 px-2 text-left min-w-[170px]">Dates</th>
                                <th className="py-4 px-2 text-left min-w-[60px]">Jours</th>
                                <th className="py-4 px-2 text-left min-w-[140px]">Solde CP</th>
                                <th className="py-4 px-2 text-left min-w-[100px]">Statut</th>
                                <th className="py-4 px-2 pl-6 text-right rounded-tr-xl min-w-[170px]">Action</th>
                             </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 text-sm">
                             {enrichedTeamRequests.map(req => (
                                <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                                   <td className="py-4 pl-4 pr-2"><div className="font-bold text-gray-800">{req.user_name}</div></td>
                                   <td className="py-4 px-2"><LeaveTypeBadge type={req.leave_type} />{req.is_anticipated && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200 uppercase">Anticipé</span>}</td>
                                   <td className="py-4 px-2 font-medium text-gray-700 whitespace-nowrap">{new Date(req.start_datetime ? req.start_datetime.slice(0,10) : req.start_date).toLocaleDateString('fr-FR')} – {new Date(req.end_datetime ? req.end_datetime.slice(0,10) : req.end_date).toLocaleDateString('fr-FR')}</td>
                                   <td className="py-4 px-2 font-bold text-gray-800">{fmtDays(req.days_count)} j</td>
                                   <td className="py-4 px-2">
                                     {req.leave_type === 'paid_leave' && req.balance_before !== null ? (
                                       <div className="flex items-center gap-1.5 text-xs">
                                         <span className="text-gray-500 font-medium">{fmtDays(req.balance_before)} j</span>
                                         <span className="text-gray-300">→</span>
                                         <span className={`font-bold ${req.balance_after < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmtDays(req.balance_after)} j</span>
                                         {req.balance_after < 0 && <span className="text-[9px] font-bold bg-red-100 text-red-600 border border-red-200 rounded px-1">⚠</span>}
                                       </div>
                                     ) : <span className="text-gray-400 text-xs">—</span>}
                                   </td>
                                   <td className="py-4 px-2">{getStatusBadge(req.status)}</td>
                                   <td className="py-4 pr-4 pl-6 text-right whitespace-nowrap space-x-2">
                                      {req.status === 'pending' && (
                                         <>
                                          <button onClick={() => handleApprove(req.id)} className="px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg text-xs font-bold transition-colors">Approuver</button>
                                          <button onClick={() => handleReject(req.id)} className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs font-bold transition-colors">Refuser</button>
                                         </>
                                      )}
                                      {req.status !== 'cancelled' && req.status !== 'rejected' && (
                                         <button onClick={() => handleManagerCancel(req.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors inline-block align-middle ml-2"><Trash2 className="w-4 h-4" /></button>
                                      )}
                                   </td>
                                </tr>
                             ))}
                             {enrichedTeamRequests.length === 0 && <tr><td colSpan="7" className="py-12 text-center text-gray-400">Aucune demande trouvée.</td></tr>}
                          </tbody>
                       </table>
                     </div>
                   </>
                 )}
              </div>
           </div>
         )}
      </div>
    </div>
  );
}
